import type {
  CreatePrototypePayload,
  Paginated,
  Prototype,
  UpdatePrototypePayload,
} from '@atlas/api-contract';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '@/lib/api/errors';
import {
  createPrototype,
  deletePrototype,
  getPrototype,
  listPrototypes,
  updatePrototype,
} from '@/lib/api/prototypes';
import { queryKeys } from '@/lib/query/keys';

import { prototypesSearchCodec, type PrototypesSearch } from './prototypes-search';

/**
 * React Query bindings for a project's prototypes.
 *
 * Nothing here is optimistic, for the same reason nothing in `projects-queries`
 * is: every mutation either creates a row with a server-assigned id or destroys
 * a subtree of other people's work. The response is awaited, then the cache is
 * invalidated, and `onSuccess` returns the invalidation promise so `mutateAsync`
 * settles only once the list has caught up.
 *
 * Creating and deleting also invalidate the *project* list, because a project
 * card shows its prototype count — leaving it stale would mean navigating back
 * to a card that disagrees with the page just left.
 */

/** Query options for one page of the list, reusable from a route loader. */
export const prototypesQueryOptions = (
  projectId: string,
  search: PrototypesSearch,
) => {
  const query = prototypesSearchCodec.toQuery(search);

  return queryOptions<Paginated<Prototype>, ApiError>({
    queryKey: queryKeys.prototypes.list(projectId, query),
    queryFn: ({ signal }) => listPrototypes(projectId, query, { signal }),
    /* Keeps the previous page on screen, dimmed, while the next one loads. */
    placeholderData: keepPreviousData,
  });
};

export const usePrototypesQuery = (
  projectId: string,
  search: PrototypesSearch,
): UseQueryResult<Paginated<Prototype>, ApiError> =>
  useQuery(prototypesQueryOptions(projectId, search));

/**
 * One prototype. 404 when the id names nothing.
 *
 * The viewer route depends on this being the request that 404s: the version
 * list answers `200` with an empty array for an unknown prototype, which is
 * indistinguishable from a real prototype nobody has uploaded to yet.
 */
export const prototypeQueryOptions = (prototypeId: string) =>
  queryOptions<Prototype, ApiError>({
    queryKey: queryKeys.prototypes.detail(prototypeId),
    queryFn: ({ signal }) => getPrototype(prototypeId, { signal }),
  });

export const usePrototypeQuery = (
  prototypeId: string,
): UseQueryResult<Prototype, ApiError> =>
  useQuery(prototypeQueryOptions(prototypeId));

export interface CreatePrototypeVariables {
  projectId: string;
  payload: CreatePrototypePayload;
}

export const useCreatePrototype = (): UseMutationResult<
  Prototype,
  ApiError,
  CreatePrototypeVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<Prototype, ApiError, CreatePrototypeVariables>({
    mutationFn: ({ projectId, payload }) => createPrototype(projectId, payload),
    onSuccess: (_prototype, { projectId }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.prototypes.lists(projectId),
        }),
        // The project card counts prototypes.
        queryClient.invalidateQueries({ queryKey: queryKeys.projects.lists() }),
      ]),
  });
};

export interface UpdatePrototypeVariables {
  prototypeId: string;
  /** Carried only so the right list can be invalidated. */
  projectId: string;
  payload: UpdatePrototypePayload;
}

export const useUpdatePrototype = (): UseMutationResult<
  Prototype,
  ApiError,
  UpdatePrototypeVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<Prototype, ApiError, UpdatePrototypeVariables>({
    mutationFn: ({ prototypeId, payload }) => updatePrototype(prototypeId, payload),
    onSuccess: (prototype, { projectId }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.prototypes.lists(projectId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.prototypes.detail(prototype.id),
        }),
      ]),
  });
};

export interface DeletePrototypeVariables {
  prototypeId: string;
  projectId: string;
}

export const useDeletePrototype = (): UseMutationResult<
  void,
  ApiError,
  DeletePrototypeVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, DeletePrototypeVariables>({
    mutationFn: ({ prototypeId }) => deletePrototype(prototypeId),
    /*
     * The delete cascades to versions, stored files, threads and comments, so
     * anything cached below this prototype now describes rows that are gone.
     * Invalidating the whole tree is the only correct answer — and it picks up
     * the project's prototype count on the way.
     */
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.all() }),
  });
};
